import { createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { IClub, IClubFilter } from './club.interface';
import { ClubActions } from './club.actions';
import { IResponseError } from '../../service/interface';

export const clubFeatureKey = 'club';

export interface IState extends EntityState<IClub> {
	socketConnected: boolean;
	loading: boolean;
	error?: IResponseError;
	filter: IClubFilter;
}

export const clubAdapter: EntityAdapter<IClub> = createEntityAdapter<IClub>({
	selectId: (club: IClub) => club.id,
});

export const initialState: IState = clubAdapter.getInitialState({
	socketConnected: false,
	loading: false,
	filter: {
		pagination: {
			count: 0,
			limit: 10,
			offset: 1,
		},
	},
});

export const reducer = createReducer(
	initialState,
	// ── API ──

	on(
		ClubActions.loadClubs,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		ClubActions.loadClubsSuccess,
		(state, action): IState =>
			clubAdapter.setAll(action.clubs, {
				...state,
				filter: {
					...state.filter,
					pagination: {
						...state.filter.pagination,
						count: action.pagination.count,
					},
				},
				loading: false,
				error: undefined,
			}),
	),
	on(
		ClubActions.loadClubsError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),

	// ── UI ──

	on(
		ClubActions.setOffset,
		(state, action): IState => ({
			...state,
			loading: true,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: action.offset,
				},
			},
		}),
	),
	on(
		ClubActions.setLimit,
		(state, action): IState => ({
			...state,
			loading: true,
			error: undefined,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: 1,
					limit: action.limit,
				},
			},
		}),
	),
	on(
		ClubActions.setSortOrder,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: action.sortBy as IClubFilter['sortBy'],
				orderBy: action.orderBy,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		ClubActions.clearSortOrder,
		(state): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: undefined,
				orderBy: undefined,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		ClubActions.setFilter,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				...action.data,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		ClubActions.resetFilter,
		(state): IState => ({
			...state,
			filter: initialState.filter,
		}),
	),
	on(
		ClubActions.clearClubError,
		(state): IState => ({
			...state,
			error: undefined,
		}),
	),

	// ── Socket ──

	on(
		ClubActions.adminClubUpdatedViaSocket,
		(state, action): IState =>
			clubAdapter.updateOne(
				{ id: action.club.id, changes: action.club },
				state,
			),
	),
	on(
		ClubActions.socketConnect,
		ClubActions.socketReconnected,
		(state): IState => ({
			...state,
			socketConnected: true,
		}),
	),
	on(
		ClubActions.socketDisconnected,
		ClubActions.socketClose,
		(state): IState => ({
			...state,
			socketConnected: false,
		}),
	),
);
